// src/components/HR/SalaryManagementSection.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api, resolveFileUrl } from '../../apiService';
import { toast } from 'react-toastify';

const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const getEmployeeKey = (emp) => emp?.employeeId || emp?.id;

// --- Internal Component: SalaryEditModal ---
function SalaryEditModal({ isOpen, onClose, onSave, salaryRecord, employee }) {
    const [deductions, setDeductions] = useState(toNumber(salaryRecord?.deductions));
    const [bonus, setBonus] = useState(toNumber(salaryRecord?.bonus));
    const [notes, setNotes] = useState(salaryRecord?.notes || '');
    const [netSalary, setNetSalary] = useState(toNumber(salaryRecord?.netSalary) || toNumber(employee.salary));

    useEffect(() => {
        const basic = toNumber(employee.salary);
        const newNetSalary = basic + parseFloat(bonus || 0) - parseFloat(deductions || 0);
        setNetSalary(newNetSalary);
    }, [deductions, bonus, employee.salary]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({
            ...salaryRecord,
            deductions: parseFloat(deductions || 0),
            bonus: parseFloat(bonus || 0),
            netSalary,
            notes,
            status: salaryRecord.status === 'Paid' ? 'Paid' : 'Processed'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Edit Salary for {employee.fullName}</h2>
                <p className="text-gray-500 mb-6">For {new Date(salaryRecord.monthId.split('-')[0], salaryRecord.monthId.split('-')[1]-1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                
                <div className="space-y-4">
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Basic Salary (PKR)</label>
                        <input type="text" value={(employee.salary || 0).toLocaleString()} className="mt-1 block w-full p-3 bg-gray-200 rounded-md" readOnly />
                    </div>
                     <div>
                        <label htmlFor="bonus" className="block text-sm font-medium text-gray-700">Bonus / Allowance</label>
                        <input type="number" id="bonus" value={bonus} onChange={e => setBonus(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" />
                    </div>
                     <div>
                        <label htmlFor="deductions" className="block text-sm font-medium text-gray-700">Deductions</label>
                        <input type="number" id="deductions" value={deductions} onChange={e => setDeductions(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" />
                    </div>
                     <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes (Optional)</label>
                        <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows="2" className="mt-1 block w-full p-3 bg-gray-50 rounded-md"></textarea>
                    </div>
                    <div className="pt-4 border-t">
                        <div className="flex justify-between items-center text-xl font-bold">
                            <span>Net Salary:</span>
                            <span className="text-rose-600">PKR {netSalary.toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="px-5 py-2 bg-gray-300 text-gray-800 rounded-lg font-semibold">Cancel</button>
                    <button onClick={handleSave} className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold">Save Changes</button>
                </div>
            </div>
        </div>
    );
}

function SalaryManagementSection() {
    const { currentStationId } = useAppContext();
    const [employees, setEmployees] = useState([]);
    const [salaryData, setSalaryData] = useState({});
    const [attendanceSummary, setAttendanceSummary] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    
    const getInitialFilterDate = () => {
        const date = new Date();
        date.setDate(1); 
        date.setMonth(date.getMonth() - 1); 
        return { month: date.getMonth(), year: date.getFullYear() };
    };

    const [filter, setFilter] = useState(getInitialFilterDate());
    const [editingSalaryRecord, setEditingSalaryRecord] = useState(null);

    const toYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchEmployees = useCallback(async () => {
        if (!currentStationId || currentStationId === 'all') {
            setEmployees([]); setIsLoading(false); return;
        }
        setIsLoading(true);
        try {
            const data = await api.get('workers', { where: { stationId: currentStationId }, orderBy: 'fullName' });
            setEmployees(data);
        } catch (err) {
            toast.error("Could not fetch employee data.");
        } finally {
            setIsLoading(false);
        }
    }, [currentStationId]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);
    
    const fetchSalaryAndAttendanceData = useCallback(async () => {
        if (employees.length === 0) {
            setSalaryData({}); setAttendanceSummary({}); return;
        }

        const salaryMonthId = `${filter.year}-${String(filter.month + 1).padStart(2, '0')}`;
        
        try {
            const salaries = await api.get('salaries', { where: { monthId: salaryMonthId, stationId: currentStationId } });
            const salaryMap = {};
            salaries.forEach(s => { salaryMap[s.employeeId] = { ...s, netSalary: toNumber(s.netSalary), bonus: toNumber(s.bonus), deductions: toNumber(s.deductions) }; });
            setSalaryData(salaryMap);

            const startDateOfMonth = new Date(filter.year, filter.month, 1);
            const endDateOfMonth = new Date(filter.year, filter.month + 1, 0);
            const startDateStr = toYYYYMMDD(startDateOfMonth);
            const endDateStr = toYYYYMMDD(endDateOfMonth);
            
            const attendance = await api.get('attendance', { 
                where: JSON.stringify({ 
                    stationId: currentStationId,
                    date: { '>=': startDateStr, '<=': endDateStr }
                })
            });

            const summary = {};
            for (const emp of employees) {
                const today = new Date();
                let workingDaysPassed = 0;
                
                const lastDayToCheck = (today.getFullYear() > filter.year || (today.getFullYear() === filter.year && today.getMonth() > filter.month)) 
                    ? endDateOfMonth.getDate() : today.getDate();

                for (let i = 1; i <= lastDayToCheck; i++) {
                    const currentDay = new Date(filter.year, filter.month, i);
                    if (currentDay.getDay() !== 0 && currentDay.getDay() !== 6) workingDaysPassed++;
                }

                const empKey = getEmployeeKey(emp);
                const empAttendance = attendance.filter(a => a.employeeId === empKey && ['Present', 'Late', 'Completed'].includes(a.status));
                const presentDays = empAttendance.length;
                const absentDays = workingDaysPassed - presentDays;
                summary[empKey] = { present: presentDays, absent: absentDays > 0 ? absentDays : 0 };
            }
            setAttendanceSummary(summary);

        } catch (error) {
            toast.error("Failed to load salary or attendance data.");
        }
    }, [employees, filter, currentStationId]);

    useEffect(() => {
        fetchSalaryAndAttendanceData();
    }, [fetchSalaryAndAttendanceData]);

    const handlePayNow = async (employee, record) => {
        const net = toNumber(record?.netSalary);
        if (!record || !Number.isFinite(net) || net <= 0) {
            toast.error("Please 'Edit' and 'Save' the salary first to process it before paying.");
            return;
        }

        try {
            await api.put(`salaries`, record.id, { status: 'Paid', paymentDate: new Date().toISOString() });
            
            const expenseData = {
                description: `Salary for ${employee.fullName} - ${new Date(filter.year, filter.month).toLocaleString('default', { month: 'long', year: 'numeric' })}`,
                amount: net, category: 'Salaries', stationId: employee.stationId,
                date: new Date().toISOString(), salaryRecordId: record.id
            };
            await api.post('expenses', expenseData);
            toast.success(`Salary for ${employee.fullName} marked as Paid and expense recorded automatically.`);
            fetchSalaryAndAttendanceData();
        } catch (error) {
            toast.error(`Failed to process payment: ${error.message}`);
        }
    };
    
    const handleEditSalary = (employee) => {
         const salaryMonthId = `${filter.year}-${String(filter.month + 1).padStart(2, '0')}`;
         const empKey = getEmployeeKey(employee);
         const presentDays = attendanceSummary[empKey]?.present || 0;
         const absentDays = attendanceSummary[empKey]?.absent || 0;
         const deductionPerDay = toNumber(employee.salary) / 26;
         
         let record = salaryData[empKey] || {
             monthId: salaryMonthId, employeeId: empKey, stationId: employee.stationId, salary: toNumber(employee.salary),
             presentDays, bonus: 0, notes: '', status: 'Unprocessed'
         };
         
         if (record.status === 'Unprocessed') {
            record.deductions = absentDays > 0 ? Math.round(absentDays * deductionPerDay) : 0;
         }
         
        setEditingSalaryRecord({ record, employee });
    };

    const handleUpdateSalary = async (updatedRecord) => {
        try {
            if (updatedRecord.id) {
                await api.put('salaries', updatedRecord.id, updatedRecord);
            } else {
                await api.post('salaries', updatedRecord);
            }
            toast.success("Salary details updated successfully.");
            fetchSalaryAndAttendanceData();
        } catch (error) {
            toast.error(`Failed to update salary: ${error.message}`);
        } finally {
            setEditingSalaryRecord(null);
        }
    };

    const totalSalary = employees.reduce((acc, emp) => {
        const key = getEmployeeKey(emp);
        const record = salaryData[key];
        if (record && record.netSalary !== undefined && record.netSalary !== null) return acc + toNumber(record.netSalary);
        return acc + toNumber(emp.salary);
    }, 0);
    
    return (
        <div className="container mx-auto">
            {editingSalaryRecord && <SalaryEditModal isOpen={!!editingSalaryRecord} onClose={() => setEditingSalaryRecord(null)} onSave={handleUpdateSalary} salaryRecord={editingSalaryRecord.record} employee={editingSalaryRecord.employee} />}
            <div className="mb-8 flex justify-between items-center">
                 <div><h1 className="text-4xl font-bold text-gray-800">Salary Management</h1><p className="text-gray-500 mt-1">Process, track, and generate payslips for all employees.</p></div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <label htmlFor="month-filter-salary" className="block text-sm font-medium text-gray-700">Month</label>
                        <select id="month-filter-salary" value={filter.month} onChange={e => setFilter({...filter, month: parseInt(e.target.value)})} className="mt-1 block w-full p-3 bg-gray-50 rounded-md">
                            {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}
                        </select>
                    </div>
                     <div>
                        <label htmlFor="year-filter-salary" className="block text-sm font-medium text-gray-700">Year</label>
                        <select id="year-filter-salary" value={filter.year} onChange={e => setFilter({...filter, year: parseInt(e.target.value)})} className="mt-1 block w-full p-3 bg-gray-50 rounded-md">
                             {Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
                        </select>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                    {isLoading ? <p className="text-center p-8">Loading employees...</p> : (
                        <table className="w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance (P/A)</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Basic Salary</th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {employees.map(emp => {
                                    const empKey = getEmployeeKey(emp);
                                    const record = salaryData[empKey];
                                    const presentDays = attendanceSummary[empKey]?.present || 0;
                                    const absentDays = attendanceSummary[empKey]?.absent || 0;
                                    return (
                                        <tr key={emp.id || emp.employeeId}>
                                            <td className="px-4 py-3 whitespace-nowrap"><div className="flex items-center"><div className="flex-shrink-0 h-10 w-10"><img className="h-10 w-10 rounded-full object-cover" src={emp.photoURL ? resolveFileUrl(emp.photoURL) : 'https://placehold.co/40x40/E2E8F0/4A5568?text=Photo'} alt={emp.fullName}/></div><div className="ml-4"><div className="text-sm font-medium text-gray-900">{emp.fullName}</div><div className="text-sm text-gray-500">{emp.designation}</div>{record?.notes && <div className="text-xs text-blue-600 italic mt-1 max-w-xs truncate" title={record.notes}>Note: {record.notes}</div>}</div></div></td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-center"><span className="font-semibold text-green-700">{presentDays}</span> / <span className="text-red-700">{absentDays}</span></td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono text-right">{toNumber(emp.salary).toLocaleString()}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-bold font-mono text-right">{record && record.netSalary !== undefined && record.netSalary !== null ? toNumber(record.netSalary).toLocaleString() : 'N/A'}</td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${record?.status === 'Paid' ? 'bg-green-100 text-green-800' : record?.status === 'Processed' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{record?.status || 'Unprocessed'}</span></td>
                                            <td className="px-4 py-3 whitespace-nowrap text-center space-x-2">
                                                <button onClick={() => handlePayNow(emp, record)} disabled={record?.status === 'Paid'} className="text-sm bg-green-600 text-white py-1 px-3 rounded hover:bg-green-700 disabled:bg-gray-400">{record?.status === 'Paid' ? 'Paid' : 'Pay'}</button>
                                                <button onClick={() => handleEditSalary(emp)} disabled={record?.status === 'Paid'} className="text-sm bg-blue-600 text-white py-1 px-3 rounded hover:bg-blue-700 disabled:bg-gray-400">Edit</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                             <tfoot className="bg-gray-100">
                                <tr>
                                    <td colSpan="2" className="px-4 py-3 text-right font-bold">Total Monthly Salaries:</td>
                                    <td className="px-4 py-3 text-right font-bold font-mono">{totalSalary.toLocaleString()}</td>
                                    <td colSpan="3"></td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SalaryManagementSection;
